import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, formatMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import ClientFiles from "../components/ClientFiles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import {
    ArrowLeft,
    WhatsappLogo,
    Receipt,
    CalendarBlank,
    Pencil,
    Trash,
    Plus,
    CheckCircle,
    MapPin,
    Phone,
    Envelope,
    House,
    FileText,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: me } = useAuth();
    const isAdmin = me?.role === "admin";
    const [client, setClient] = useState(null);
    const [paymentDialog, setPaymentDialog] = useState(false);
    const [pAmount, setPAmount] = useState("");
    const [pMethod, setPMethod] = useState("cash");
    const [pNote, setPNote] = useState("");

    async function load() {
        const { data } = await api.get(`/clients/${id}`);
        setClient(data);
    }
    useEffect(() => {
        load();
    }, [id]);

    async function addPayment(e) {
        e.preventDefault();
        try {
            await api.post(`/clients/${id}/payments`, {
                amount: Number(pAmount),
                method: pMethod,
                note: pNote || null,
            });
            toast.success("Paiement enregistré");
            setPaymentDialog(false);
            setPAmount("");
            setPNote("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    }

    async function removePayment(pid) {
        try {
            await api.delete(`/clients/${id}/payments/${pid}`);
            toast.success("Paiement supprimé");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }

    async function sendWhatsAppReminder() {
        try {
            const { data } = await api.post(`/clients/${id}/whatsapp/reminder`, {});
            if (data.sent) {
                toast.success("Relance WhatsApp envoyée via Twilio");
            } else {
                toast.message("Twilio non configuré — ouverture du lien WhatsApp", {
                    description: data.reason,
                });
                window.open(data.link, "_blank");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }

    async function sendMonthlyRecap() {
        try {
            const { data } = await api.post(`/clients/${id}/monthly-recap/whatsapp`);
            if (data.sent) {
                toast.success("Récapitulatif mensuel envoyé via WhatsApp");
            } else {
                toast.message("Twilio non configuré — ouverture du lien WhatsApp", {
                    description: data.reason,
                });
                window.open(data.link, "_blank");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }

    async function deleteClient() {
        await api.delete(`/clients/${id}`);
        toast.success("Client supprimé");
        navigate("/clients");
    }

    if (!client)
        return (
            <Layout>
                <div className="text-ink-soft">Chargement…</div>
            </Layout>
        );

    const purchaseLabel = client.purchase_type === "terrain_maison" ? "Terrain + Maison" : "Terrain";
    const contractLabel = client.contract_type === "contrat_signe" ? "Contrat signé" : "Bon de réservation";

    return (
        <Layout>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <Link to="/clients" className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm" data-testid="back-to-clients">
                    <ArrowLeft size={16} /> Retour aux clients
                </Link>
                <div className="flex gap-2">
                    <Button asChild variant="secondary" className="bg-sand hover:bg-sand/70 text-ink" data-testid="edit-client-button">
                        <Link to={`/clients/${id}/edit`}>
                            <Pencil size={16} className="mr-2" /> Modifier
                        </Link>
                    </Button>
                    {isAdmin && (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="destructive" data-testid="delete-client-button">
                                <Trash size={16} className="mr-2" /> Supprimer
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Cette action est définitive et supprime tout l'historique des paiements.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={deleteClient} className="bg-destructive text-white" data-testid="confirm-delete-client">
                                    Supprimer
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                    )}
                </div>
            </div>

            {client.is_fully_paid && (
                <Alert className="mb-6 bg-sage/15 border-sage text-sage-dark" data-testid="alert-fully-paid">
                    <CheckCircle className="h-5 w-5 !text-sage-dark" />
                    <AlertTitle className="font-display">Paiement terminé</AlertTitle>
                    <AlertDescription>
                        Ce client a soldé l'intégralité de son dossier. Pensez à archiver et envoyer le reçu final.
                    </AlertDescription>
                </Alert>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bento-card fade-up">
                        <div className="flex items-start gap-5">
                            <div className="w-16 h-16 rounded-xl bg-terracotta text-white grid place-items-center font-display text-2xl font-bold">
                                {client.first_name?.[0]}{client.last_name?.[0]}
                            </div>
                            <div className="flex-1">
                                <h1 className="font-display text-3xl font-bold text-ink" data-testid="client-name">
                                    {client.first_name} {client.last_name}
                                </h1>
                                <div className="flex items-center gap-4 text-sm text-ink-soft mt-2 flex-wrap">
                                    <span className="flex items-center gap-1.5"><Phone size={14} /> {client.phone}</span>
                                    {client.email && <span className="flex items-center gap-1.5"><Envelope size={14} /> {client.email}</span>}
                                    <span className="flex items-center gap-1.5"><MapPin size={14} /> {client.city}</span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-4">
                                    <Badge className="bg-sand text-ink hover:bg-sand">{purchaseLabel}</Badge>
                                    <Badge className={client.contract_type === "contrat_signe" ? "bg-sage text-white" : "bg-[#E4A853] text-ink"}>{contractLabel}</Badge>
                                    {client.house_model && <Badge className="bg-terracotta text-white"><House size={12} className="mr-1" /> {client.house_model}</Badge>}
                                    {client.plot_reference && <Badge variant="outline" className="border-sand">Parcelle: {client.plot_reference}</Badge>}
                                </div>
                            </div>
                        </div>
                        {client.notes && (
                            <div className="mt-5 pt-5 border-t border-sand text-sm text-ink-soft">
                                <p className="text-xs uppercase tracking-widest mb-1.5">Notes</p>
                                {client.notes}
                            </div>
                        )}
                    </div>

                    <div className="bento-card fade-up">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl font-bold text-ink">Historique des paiements</h3>
                            <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
                                <DialogTrigger asChild>
                                    <Button className="bg-terracotta hover:bg-terracotta-dark text-white" data-testid="add-payment-button">
                                        <Plus size={16} className="mr-1.5" /> Ajouter un paiement
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Nouveau paiement</DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={addPayment} className="space-y-4">
                                        <div>
                                            <Label>Montant (USD)</Label>
                                            <Input type="number" min="0.01" step="0.01" value={pAmount} onChange={(e) => setPAmount(e.target.value)} required data-testid="payment-amount-input" />
                                        </div>
                                        <div>
                                            <Label>Mode</Label>
                                            <Select value={pMethod} onValueChange={setPMethod}>
                                                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="cash">Espèces</SelectItem>
                                                    <SelectItem value="bank_transfer">Virement</SelectItem>
                                                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                                                    <SelectItem value="check">Chèque</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Note</Label>
                                            <Textarea value={pNote} onChange={(e) => setPNote(e.target.value)} rows={2} data-testid="payment-note-input" />
                                        </div>
                                        <DialogFooter>
                                            <Button type="submit" className="bg-terracotta hover:bg-terracotta-dark text-white" data-testid="confirm-payment-button">
                                                Enregistrer
                                            </Button>
                                        </DialogFooter>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>

                        {client.payments.length === 0 ? (
                            <p className="text-sm text-ink-soft py-8 text-center">Aucun paiement enregistré.</p>
                        ) : (
                            <div className="divide-y divide-sand/60">
                                {[...client.payments].reverse().map((p) => (
                                    <div key={p.id} className="flex items-center justify-between py-3" data-testid={`payment-row-${p.id}`}>
                                        <div>
                                            <p className="font-medium text-ink">{formatMoney(p.amount)}</p>
                                            <p className="text-xs text-ink-soft">
                                                {new Date(p.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })} · {p.method}
                                                {p.note && ` · ${p.note}`}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <a
                                                href={`${BACKEND_URL}/api/clients/${id}/receipt?payment_id=${p.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs uppercase tracking-widest text-terracotta hover:underline flex items-center gap-1"
                                                data-testid={`receipt-link-${p.id}`}
                                            >
                                                <Receipt size={14} /> Reçu PDF
                                            </a>
                                            <button onClick={() => removePayment(p.id)} className="text-ink-soft hover:text-destructive p-1" data-testid={`delete-payment-${p.id}`}>
                                                <Trash size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <ClientFiles clientId={id} />
                </div>

                <div className="space-y-6">
                    <div className="bento-card fade-up">
                        <h3 className="font-display text-lg font-bold text-ink mb-4">Avancement du paiement</h3>
                        <div className="text-center">
                            <p className="font-display text-5xl font-bold text-terracotta" data-testid="progress-percentage">
                                {client.progress}%
                            </p>
                            <p className="text-xs uppercase tracking-widest text-ink-soft mt-1">Complété</p>
                        </div>
                        <Progress
                            value={client.progress}
                            className={`h-3 mt-4 ${client.is_fully_paid ? "[&>div]:bg-sage" : "[&>div]:bg-terracotta"}`}
                        />
                        <div className="grid grid-cols-3 gap-2 mt-5 text-center">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-ink-soft">Total</p>
                                <p className="text-sm font-semibold text-ink mt-1">{formatMoney(client.total_amount)}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-sage-dark">Payé</p>
                                <p className="text-sm font-semibold text-sage-dark mt-1" data-testid="paid-amount">{formatMoney(client.paid_amount)}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-terracotta-dark">Reste</p>
                                <p className="text-sm font-semibold text-terracotta-dark mt-1" data-testid="remaining-amount">{formatMoney(client.remaining)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card fade-up space-y-3">
                        <h3 className="font-display text-lg font-bold text-ink">Actions</h3>
                        <Button
                            onClick={sendWhatsAppReminder}
                            className="w-full bg-whatsapp hover:bg-[#20BD5A] text-white justify-start"
                            data-testid="whatsapp-reminder-button"
                        >
                            <WhatsappLogo size={18} weight="fill" className="mr-2" />
                            Relance WhatsApp
                        </Button>
                        <Button
                            onClick={sendMonthlyRecap}
                            variant="secondary"
                            className="w-full bg-sand hover:bg-sand/70 text-ink justify-start"
                            data-testid="monthly-recap-whatsapp-button"
                        >
                            <CalendarBlank size={18} className="mr-2" />
                            Récap mensuel (WhatsApp)
                        </Button>
                        <a
                            href={`${BACKEND_URL}/api/clients/${id}/receipt`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 w-full justify-start px-4 py-2.5 rounded-md bg-sand hover:bg-sand/70 text-ink text-sm font-medium"
                            data-testid="global-receipt-button"
                        >
                            <Receipt size={18} /> Reçu global PDF
                        </a>
                        <a
                            href={`${BACKEND_URL}/api/clients/${id}/monthly-recap`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 w-full justify-start px-4 py-2.5 rounded-md bg-sand hover:bg-sand/70 text-ink text-sm font-medium"
                            data-testid="monthly-recap-pdf-button"
                        >
                            <FileText size={18} /> Récap mensuel PDF
                        </a>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
